import React, { useState } from 'react'

import { getErrorMessage } from 'utils'

import { useSnackbar } from 'contexts'
import { useCompanyMutationsQuery } from 'hooks/api'

import { Stack, Typography } from '@mui/material'

import { Modal, Button, Alert, Input, TextArea } from '@repo/ui'
import { SelectField } from 'components'
import type { Company } from 'types'

const SUSPEND_REASONS = [
  { label: 'Non-payment', value: 'Non-payment' },
  { label: 'Abuse', value: 'Abuse' },
  { label: 'Customer request', value: 'Customer request' },
  { label: 'Other', value: 'Other' },
]

interface SuspendAccessModalProps {
  company: Company | null
  open: boolean
  onClose: () => void
  refetch: () => void
}

export const SuspendAccessModal = (props: SuspendAccessModalProps) => {
  const { company, open, onClose = () => {}, refetch } = props

  const { open: notification } = useSnackbar()

  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')

  const { suspend, isLoading } = useCompanyMutationsQuery({
    onSuccess: () => {
      handleClose()
      refetch()
    },
    onError: error => {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    },
  })

  const handleSuspendAccess = () => {
    if (!company?.id) return
    const fullReason = reason
      ? note.trim()
        ? `${reason} — ${note.trim()}`
        : reason
      : undefined
    suspend({ companyId: company.id, reason: fullReason })
  }

  const handleClose = () => {
    setReason('')
    setNote('')
    onClose()
  }

  const hasActiveSubscription =
    !!company?.subscription && company.subscription.status === 'active'

  return (
    <Modal
      title="Suspend access"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            destructive
            onClick={handleSuspendAccess}
            disabled={isLoading || !reason}
          >
            {isLoading ? 'Suspending...' : 'Suspend'}
          </Button>
        </>
      }
    >
      <Stack spacing="16px">
        <Typography variant="body2" color="text.secondary">
          This immediately blocks all access to{' '}
          <strong>{company?.name}</strong> for everyone in the org. It&apos;s
          reversible at any time via Unsuspend.
        </Typography>

        {hasActiveSubscription && (
          <Alert
            severity="warning"
            description="This org has an active subscription — billing continues. Use Cancel subscription to stop charges."
          />
        )}

        <SelectField
          label="Reason"
          value={reason}
          onChange={setReason}
          options={SUSPEND_REASONS}
        />

        <TextArea
          label="Note (optional)"
          placeholder="Add additional context…"
          value={note}
          onValueChange={setNote}
          minRows={3}
        />
      </Stack>
    </Modal>
  )
}
